// src/components/theme/ThemeText.js

import React, { useRef, useState, useEffect } from 'react';
import { Group, Text, Rect } from 'react-konva';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';

const scaleFactor = 1.9;

// Mapping between integers and alignment strings
const TEXT_ALIGN_MAP = {
  0: 'left',
  1: 'center',
  2: 'right',
};


  // Function to determine the unit text based on unitType and main unit settings
  const getUnitText = (unitType, distanceUnit, temperatureUnit) => {
    console.log('getUnitText called with:', unitType, distanceUnit, temperatureUnit);

    switch (unitType) {
      case 1: // Speed
        return distanceUnit === 'metric' ? 'Km/h' : 'Mph';
      case 2: // Distance
        return distanceUnit === 'metric' ? 'Km' : 'Mi';
      case 3: // Distance Auto
        return distanceUnit === 'metric' ? 'Km' : 'Mi';
      case 4: // Temperature
        return temperatureUnit === 'celsius' ? '°C' : '°F';
      case 5: // Voltage
        return 'V';
      case 6: // Current
        return 'A';
      case 7: // Humidity
        return '%';
      case 8: // Power
        return 'W';
      case 9: // Power (kW)
        return 'kW';
      case 10: // Autonomy (%)
        return '%';
      case 11: // Reg txt conditionnal
        return 'Reg';
      case 12: // Reg speed conditional
        return distanceUnit === 'metric' ? 'Km/h' : 'Mph';
      case 13: // MAC Address
        return '01:02:03:04:05:06';
      default:
        console.warn(`Unknown unitType: ${unitType}. Defaulting to "None".`);
        return '-';
    }
  };

// ThemeTextSettings Component
export const ThemeTextSettings = ({ element, updateElement, distanceUnit, temperatureUnit }) => {
  // Handle Text Content Change
  const handleTextChange = (e) => {
    const newText = e.target.value.slice(0, 64); // Limit text to 64 characters
    updateElement(element.id, 'text', newText);
  };

  // Handle Font Size Change
  const handleFontSizeChange = (e) => {
    const fontSize = parseInt(e.target.value, 10);
    if (!isNaN(fontSize) && [10, 14, 18].includes(fontSize)) {
      updateElement(element.id, 'size', fontSize);
    }
  };

  // Handle X Position Change
  const handleXChange = (e) => {
    const newX = parseInt(e.target.value, 10);
    if (!isNaN(newX)) {
      updateElement(element.id, 'x', newX);
    }
  };

  // Handle Y Position Change
  const handleYChange = (e) => {
    const newY = parseInt(e.target.value, 10);
    if (!isNaN(newY)) {
      updateElement(element.id, 'y', newY);
    }
  };


  // Handle Brake Enable Change
  const handleBrakeEnableChange = (e) => {
    const isEnabled = e.target.checked;
    updateElement(element.id, 'brakeEnabled', isEnabled);
    if (!isEnabled) {
      updateElement(element.id, 'brakeText', '-');
      updateElement(element.id, 'brakeUnitType', 0); // Reset brakeUnitType to 'None'
    }
  };

  // Handle Brake Text Change
  const handleBrakeTextChange = (e) => {
    const newBrakeText = e.target.value.slice(0, 64); // Limit brake text to 64 characters
    updateElement(element.id, 'brakeText', newBrakeText);
  };

  // Handle Brake Unit Type Change
  const handleBrakeUnitTypeChange = (e) => {
    const newBrakeUnitType = parseInt(e.target.value, 10);
    console.log('handleBrakeUnitTypeChange:', newBrakeUnitType, distanceUnit, temperatureUnit);
    updateElement(element.id, 'brakeUnitType', newBrakeUnitType);

    if (newBrakeUnitType !== 0) {
      const forcedBrakeText = getUnitText(newBrakeUnitType, distanceUnit, temperatureUnit);
      console.log('Forced Brake Text:', forcedBrakeText);
      updateElement(element.id, 'brakeText', forcedBrakeText);
    } else {
      // Optionally, reset brake text if brakeUnitType is set to 'None'
      updateElement(element.id, 'brakeText', '-');
    }
  };

  // Handle Text Alignment Change
  const handleTextAlignChange = (e) => {
    const alignValue = parseInt(e.target.value, 10);
    if ([0, 1, 2].includes(alignValue)) {
      updateElement(element.id, 'textAlign', alignValue);
    }
  };

  // Handle Unit Type Change
  const handleUnitTypeChange = (e) => {
    const newUnitType = parseInt(e.target.value, 10);
    console.log('handleUnitTypeChange:', newUnitType, distanceUnit, temperatureUnit);
    updateElement(element.id, 'unitType', newUnitType);

    if (newUnitType !== 0) {
      const forcedText = getUnitText(newUnitType, distanceUnit, temperatureUnit);
      console.log('Forced Text:', forcedText);
      updateElement(element.id, 'text', forcedText);
    } else {
      // Optionally, reset text if unit type is set to 'None'
      // updateElement(element.id, 'text', '-');
    }
  };


  useEffect(() => {
    console.log('useEffect called with:', distanceUnit, temperatureUnit);
    // Optionally, refresh text based on current unit settings
    if (element.unitType && element.unitType !== 0) {
      const updatedText = getUnitText(element.unitType, distanceUnit, temperatureUnit);
      updateElement(element.id, 'text', updatedText);
    }
    if (element.brakeUnitType && element.brakeUnitType !== 0) {
      const updatedBrakeText = getUnitText(element.brakeUnitType, distanceUnit, temperatureUnit);
      updateElement(element.id, 'brakeText', updatedBrakeText);
    }
  }, [distanceUnit, temperatureUnit]);

  return (
    <div>
      {/* Position Controls */}
      <label>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
          style={{ marginLeft: '10px', width: '60px' }}
        />
      </label>

      <label>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
          style={{ marginLeft: '10px', width: '60px' }}
        />
      </label>

      {/* Unit Type Selector */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Unit Type:
        <select
          className={styles.select200}
          value={element.unitType && element.unitType || 0}
          onChange={handleUnitTypeChange}
          style={{ marginLeft: '10px' }}
        >
          <option value={0}>None</option>
          <option value={1}>Speed (Km/h / Mph)</option>
          <option value={2}>Distance (Km / Mi)</option>
          <option value={3}>Distance Auto (m-Km / ft-Mi)</option>
          <option value={4}>Temperature (°C / °F)</option>
          <option value={5}>Voltage (V)</option>
          <option value={6}>Current (A)</option>
          <option value={7}>Humidity (%)</option>
          <option value={8}>Power (W)</option>
          <option value={9}>Power (kW)</option>
          <option value={10}>Autonomy (%)</option>
          <option value={11}>Reg txt conditionnal</option>
          <option value={12}>Speed (Km/h / Mph) reg conditionnal</option>
          <option value={13}>MAC address</option>
        </select>
      </label>

      {/* Text Content */}
      <label>
        Text Content (max 64 chars):
        <input
          className={styles.inputText}
          type="text"
          value={element.text}
          onChange={handleTextChange}
          style={{ marginLeft: '10px', width: '200px' }}
          maxLength={64} // Enforces maximum input length
          placeholder="Enter text here"
          disabled={element.unitType && element.unitType !== 0} // Disable if unitType is not 'None'
        />
      </label>

      {/* Brake Text Toggle */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Enable Brake Text:
        <Checkbox
          checked={element.brakeEnabled || false}
          onChange={handleBrakeEnableChange}
          style={{ marginLeft: '10px' }}
        />
      </label>
      {element.brakeEnabled && (
        <div style={{ marginTop: '10px', paddingLeft: '20px', borderLeft: '10px solid #9013FE' }}>
          {/* Brake Unit Type Selector */}
          <label style={{ display: 'block', marginTop: '10px' }}>
            Brake Unit Type:
            <select
              className={styles.select200}
              value={element.brakeUnitType && element.brakeUnitType || 0}
              onChange={handleBrakeUnitTypeChange}
              style={{ marginLeft: '10px', width: '220px' }}
            >
              <option value={0}>None</option>
              <option value={1}>Speed (Km/h / Mph)</option>
              <option value={2}>Distance (Km / Mi)</option>
              <option value={3}>Distance Auto (m-Km / ft-Mi)</option>
              <option value={4}>Temperature (°C / °F)</option>
              <option value={5}>Voltage (V)</option>
              <option value={6}>Current (A)</option>
              <option value={7}>Humidity (%)</option>
              <option value={8}>Power (W)</option>
              <option value={9}>Power (kW)</option>
              <option value={10}>Autonomy (%)</option>
              <option value={11}>Reg txt conditionnal</option>
              <option value={12}>Speed (Km/h / Mph) reg conditionnal</option>
              <option value={13}>MAC address</option>
            </select>
          </label>

          {/* Brake Text Content */}
          <label>
            Brake Text (max 64 chars):
            <input
              className={styles.inputText}
              type="text"
              value={element.brakeText}
              onChange={handleBrakeTextChange}
              style={{ marginLeft: '10px', width: '200px' }}
              maxLength={64} // Enforces maximum input length
              placeholder="Enter brake text"
              disabled={element.brakeUnitType && element.brakeUnitType !== 0} // Disable if brakeUnitType is not 'None'
            />
          </label>
        </div>
      )}

      {/* Font Size */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Font Size:
        <select
          className={styles.inputNumber}
          value={element.size}
          onChange={handleFontSizeChange}
          style={{ marginLeft: '10px', width: '100px' }}
        >
          <option value={10}>10</option>
          <option value={14}>14</option>
          <option value={18}>18</option>
        </select>
      </label>

      {/* Text Alignment */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Text Alignment:
        <select
          className={styles.select200}
          value={element.textAlign}
          onChange={handleTextAlignChange}
          style={{ marginLeft: '10px', width: '100px' }}
        >
          <option value={0}>Left</option>
          <option value={1}>Center</option>
          <option value={2}>Right</option>
        </select>
      </label>

      {/* Color */}
      <CustomColorPicker
        label="Color"
        color={element.color}
        onChange={(value) => { updateElement(element.id, 'color', value); }}
      />
    </div>
  );
};

ThemeTextSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    unitType: PropTypes.number, // Changed to number
    brakeUnitType: PropTypes.number, // Changed to number
    size: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    brakeEnabled: PropTypes.bool,
    brakeText: PropTypes.string,
    textAlign: PropTypes.number.isRequired, // Stored as an integer
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
  distanceUnit: PropTypes.oneOf(['metric', 'imperial']).isRequired, // New prop
  temperatureUnit: PropTypes.oneOf(['celsius', 'fahrenheit']).isRequired, // New prop
};


// ThemeText Component
const ThemeText = ({
  element,
  setSelectedElement,
  selectedElement,
  updateElement,
  isBrakeActive,
  distanceUnit,
  temperatureUnit,
  render,editAllowed,
  scale, 
  snapToGridEnabled, 
  gridSize
}) => {
  const textRef = useRef(null);
  const [textDimensions, setTextDimensions] = useState({ width: 0, height: 0 });
  const [fontLoaded, setFontLoaded] = useState(false);

  const displayText =
    isBrakeActive && element.brakeEnabled && element.brakeText
      ? element.brakeText
      : element.text;

  const handleDragEnd = (e) => {
    const { x, y } = e.target.position();
    updateElement(element.id, 'x', Math.round(x));
    updateElement(element.id, 'y', Math.round(y));
  };

  useEffect(() => {
    if (document.fonts) {
      document.fonts.load(`10pt 'ForcedSquare'`).then(() => {
        setFontLoaded(true);
      });
    } else {
      setFontLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (fontLoaded && textRef.current) {
      const textWidth = textRef.current.width() - 2;
      const textHeight = textRef.current.height();
      setTextDimensions({ width: textWidth, height: textHeight });
    }
  }, [fontLoaded, displayText, element.size]);

  // Determine if this element is selected
  const isSelected = selectedElement && selectedElement.id === element.id;

  const alignment = TEXT_ALIGN_MAP[element.textAlign || 0]; // Default to 'left'
  let offsetX = 0;
  let textX = 0;

  if (alignment === 'center') {
    offsetX = textDimensions.width / 2;
    textX = -textDimensions.width / 2;
  } else if (alignment === 'right') {
    offsetX = textDimensions.width;
    textX = -textDimensions.width;
  }

  // Update text when distanceUnit or temperatureUnit changes
  useEffect(() => {
    if (element.unitType && element.unitType !== 0) {
      const updatedText = getUnitText(element.unitType, distanceUnit, temperatureUnit);
      if (updatedText !== element.text) {
        updateElement(element.id, 'text', updatedText);
      }
    }
    if (element.brakeUnitType && element.brakeUnitType !== 0 && element.brakeEnabled) {
      const updatedBrakeText = getUnitText(element.brakeUnitType, distanceUnit, temperatureUnit);
      if (updatedBrakeText !== element.brakeText) {
        updateElement(element.id, 'brakeText', updatedBrakeText);
      }
    }
  }, [distanceUnit, temperatureUnit]);

  return (
    <Group
      x={element.x}
      y={element.y}
      draggable={!render && editAllowed}
      dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, SCREEN_WIDTH, SCREEN_HEIGHT, 2, element.size) }}
      onClick={() => setSelectedElement(element)}
      onDragEnd={handleDragEnd}
    >
      {fontLoaded && (
        <Text
          ref={textRef}
          text={displayText}
          fontSize={element.size * scaleFactor}
          fill={element.color}
          fontFamily="'ForcedSquare', monospace"
          draggable={false}
          align={alignment}
          offsetX={offsetX}
          offsetY={element.size * 0.2}
        />
      )}
      {!render && fontLoaded && textDimensions.width > 0 && textDimensions.height > 0 && (
        <Rect
          x={textX}
          y={0}
          width={textDimensions.width}
          height={textDimensions.height * 0.82}
          stroke={"#1976d2"}
          dash={isSelected ? [1, 1] : [0, 0]}
          strokeWidth={0.5}
          listening={false}
        />
      )}
      {/* Do NOT render ThemeTextSettings here */}
    </Group>
  );
};

ThemeText.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    unitType: PropTypes.number,
    brakeUnitType: PropTypes.number,
    size: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    brakeEnabled: PropTypes.bool,
    brakeText: PropTypes.string,
    textAlign: PropTypes.number.isRequired,
  }).isRequired,
  setSelectedElement: PropTypes.func.isRequired,
  selectedElement: PropTypes.object, // Adjust as needed
  updateElement: PropTypes.func.isRequired,
  isBrakeActive: PropTypes.bool.isRequired,
  distanceUnit: PropTypes.oneOf(['metric', 'imperial']).isRequired,
  temperatureUnit: PropTypes.oneOf(['celsius', 'fahrenheit']).isRequired,
  render: PropTypes.bool.isRequired,
  editAllowed: PropTypes.bool,
};

export default ThemeText;
